import { readdirSync, existsSync } from 'fs';
import { join } from 'path';
import { writeFile } from './files.js';
import { getFrameworkComponent } from './framework-registry-service.js';

/**
 * Provider configuration from registry
 */
interface ProviderConfig {
  import: string;
  function: string;
}

/**
 * Component with provider information
 */
interface ComponentWithProvider {
  componentKey: string;
  componentName: string;
  provider: ProviderConfig;
}

/**
 * Generate or update the components/ui/index.ts file with auto-generated providers
 * This implements Solution 4 - dynamic provider management based on installed components
 *
 * @param componentsUiPath - Path to components/ui directory
 * @param framework - Framework (should be 'angular')
 * @returns Success boolean
 */
export function generateAngularProvidersIndex(
  componentsUiPath: string,
  framework: 'angular' = 'angular',
): boolean {
  try {
    // Scan components/ui directory to find all installed components
    if (!existsSync(componentsUiPath)) {
      console.log(`Components directory does not exist: ${componentsUiPath}`);
      return false;
    }

    const installedComponents = readdirSync(componentsUiPath, {
      withFileTypes: true,
    })
      .filter((dirent) => dirent.isDirectory())
      .map((dirent) => dirent.name)
      .filter((name) => !name.startsWith('.') && !name.startsWith('_'));

    // For each installed component, check if it has provider configuration
    const componentsWithProviders: ComponentWithProvider[] = [];

    for (const componentKey of installedComponents) {
      const component = getFrameworkComponent(framework, componentKey);

      if (component && component.providers) {
        componentsWithProviders.push({
          componentKey,
          componentName: component.name,
          provider: component.providers,
        });
      }
    }

    // Generate the index.ts file content
    const content = generateIndexContent(
      installedComponents,
      componentsWithProviders,
    );

    // Write the file
    const indexPath = join(componentsUiPath, 'index.ts');
    writeFile(indexPath, content);

    return true;
  } catch (error) {
    console.error('Failed to generate Angular providers index:', error);
    return false;
  }
}

/**
 * Generate the content for the auto-generated index.ts file
 *
 * @param allComponents - All installed component keys
 * @param componentsWithProviders - Components that have provider configuration
 * @returns Generated file content
 */
function generateIndexContent(
  allComponents: string[],
  componentsWithProviders: ComponentWithProvider[],
): string {
  const lines: string[] = [];

  // Header comment
  lines.push('// Auto-generated by galaxy-design CLI - DO NOT EDIT MANUALLY');
  lines.push(
    '// This file is regenerated every time you add or remove components',
  );
  lines.push('');

  // Export all components
  lines.push('// Component exports');
  for (const componentKey of allComponents.sort()) {
    lines.push(`export * from './${componentKey}';`);
  }

  // Only add providers section if there are components with providers
  if (componentsWithProviders.length > 0) {
    lines.push('');
    lines.push(
      '// Provider imports for components that require Angular providers',
    );

    // Add import statements
    const uniqueImports = new Set<string>();
    for (const { provider } of componentsWithProviders) {
      uniqueImports.add(provider.import);
    }

    for (const importStatement of Array.from(uniqueImports).sort()) {
      lines.push(importStatement);
    }

    lines.push('');
    lines.push('/**');
    lines.push(
      ' * Provide all required Angular providers for Galaxy UI components',
    );
    lines.push(' * Add this to your app.config.ts providers array:');
    lines.push(' * ');
    lines.push(' * ```typescript');
    lines.push(' * export const appConfig: ApplicationConfig = {');
    lines.push(' *   providers: [');
    lines.push(' *     provideGalaxyComponents(),');
    lines.push(' *     // ... other providers');
    lines.push(' *   ]');
    lines.push(' * };');
    lines.push(' * ```');
    lines.push(' * ');
    lines.push(
      ` * This function provides configuration for: ${componentsWithProviders.map((c) => c.componentName).join(', ')}`,
    );
    lines.push(' */');
    lines.push('export function provideGalaxyComponents() {');
    lines.push('  return [');

    // Add provider function calls
    for (const { provider } of componentsWithProviders) {
      lines.push(`    ${provider.function},`);
    }

    lines.push('  ];');
    lines.push('}');
  } else {
    lines.push('');
    lines.push(
      '// No components requiring Angular providers are currently installed',
    );
  }

  lines.push('');

  return lines.join('\n');
}

/**
 * Check if a component has provider configuration
 *
 * @param componentKey - Component key
 * @param framework - Framework
 * @returns True if component has providers
 */
export function componentHasProviders(
  componentKey: string,
  framework: 'angular' = 'angular',
): boolean {
  const component = getFrameworkComponent(framework, componentKey);
  return !!(component && component.providers);
}
